import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  Tag, 
  Modal,
  Tree,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Drawer,
  Descriptions,
  Timeline,
  Badge,
  Tooltip,
  Collapse
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  EyeOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  BranchesOutlined
} from '@ant-design/icons';
import { auditService } from '../../services/auditService';
import './TaxAudit.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;
const { Panel } = Collapse;

// Mock tax calculation transaction data following PRD specifications
const mockTaxCalculationTransactions = [
  {
    id: 'txn_20241223_001',
    timestamp: '2024-12-23T14:30:15Z',
    merchantId: 'merchant_vapestore_ny',
    merchantName: 'Empire Vape Store',
    
    // Product details
    product: {
      id: 'prod_vape_eliquid_001',
      name: 'Premium Strawberry E-Liquid 50ml',
      sku: 'VEL-STRA-50',
      description: '50ml nicotine salt e-liquid, 20mg/ml strength'
    },
    
    // AI Categorization step
    aiCategorization: {
      confidence: 0.89,
      suggestedCategoryId: 'VAPE_ELIQUIDS',
      suggestedCategoryPath: 'Vaping Products > E-Liquids',
      flags: [],
      processingTime: '234ms'
    },
    
    // Selected category
    selectedCategory: {
      id: 'VAPE_ELIQUIDS',
      name: 'E-Liquids',
      path: 'Vaping Products > E-Liquids',
      source: 'ai_suggestion' // 'ai_suggestion', 'manual_override', 'admin_review'
    },
    
    // Applicable tax rules (in precedence order)
    applicableRules: [
      {
        id: 'excise_federal_vaping',
        name: 'Federal Vaping Products Excise Tax',
        type: 'excise',
        jurisdiction: 'Federal',
        rate: 0.05,
        unit: '1ml',
        calculationMethod: 'specific',
        matchReason: 'Category match: VAPE_ELIQUIDS',
        priority: 100
      },
      {
        id: 'excise_ny_vaping',
        name: 'New York Vaping Products Sales Tax',
        type: 'excise',
        jurisdiction: 'NY',
        rate: 20,
        unit: '%',
        calculationMethod: 'ad_valorem',
        matchReason: 'Geographic match: NY state',
        priority: 90
      },
      {
        id: 'sales_ny_general',
        name: 'New York General Sales Tax',
        type: 'sales',
        jurisdiction: 'NY',
        rate: 8.25,
        unit: '%',
        calculationMethod: 'ad_valorem',
        matchReason: 'Default sales tax for NY',
        priority: 50
      }
    ],
    
    // Final calculation breakdown
    calculation: {
      productPrice: 29.99,
      quantity: 1,
      
      exciseTaxes: [
        {
          ruleId: 'excise_federal_vaping',
          ruleName: 'Federal Vaping Products Excise Tax',
          calculation: '50ml × $0.05/ml = $2.50',
          amount: 2.50
        },
        {
          ruleId: 'excise_ny_vaping', 
          ruleName: 'New York Vaping Products Sales Tax',
          calculation: '$29.99 × 20% = $6.00',
          amount: 6.00
        }
      ],
      
      salesTaxes: [
        {
          ruleId: 'sales_ny_general',
          ruleName: 'New York General Sales Tax', 
          calculation: '($29.99 + $2.50) × 8.25% = $2.68',
          taxableBase: 32.49,
          amount: 2.68
        }
      ],
      
      totalExciseTax: 8.50,
      totalSalesTax: 2.68,
      totalTax: 11.18,
      finalTotal: 41.17
    },
    
    // Status and flags
    status: 'completed',
    hasErrors: false,
    hasWarnings: false,
    flags: [],
    
    // Processing metadata
    processingTime: '47ms',
    apiVersion: '2.1.0',
    requestId: 'req_20241223_14301_xyz'
  },
  
  {
    id: 'txn_20241223_002',
    timestamp: '2024-12-23T15:45:22Z',
    merchantId: 'merchant_gunstore_tx',
    merchantName: 'Lone Star Firearms',
    
    product: {
      id: 'prod_firearm_handgun_001',
      name: 'Smith & Wesson M&P 9mm',
      sku: 'SW-MP-9MM-001',
      description: 'Semi-automatic pistol, 9mm caliber'
    },
    
    aiCategorization: {
      confidence: 0.95,
      suggestedCategoryId: 'FIREARMS_HANDGUNS',
      suggestedCategoryPath: 'Firearms > Handguns',
      flags: ['high_regulation'],
      processingTime: '189ms'
    },
    
    selectedCategory: {
      id: 'FIREARMS_HANDGUNS',
      name: 'Handguns',
      path: 'Firearms > Handguns',
      source: 'ai_suggestion'
    },
    
    applicableRules: [
      {
        id: 'excise_federal_firearms',
        name: 'Federal Firearms Excise Tax',
        type: 'excise',
        jurisdiction: 'Federal',
        rate: 10,
        unit: '%',
        calculationMethod: 'ad_valorem',
        matchReason: 'Category match: FIREARMS_HANDGUNS',
        priority: 100
      },
      {
        id: 'sales_tx_general',
        name: 'Texas General Sales Tax',
        type: 'sales',
        jurisdiction: 'TX',
        rate: 6.25,
        unit: '%',
        calculationMethod: 'ad_valorem',
        matchReason: 'Default sales tax for TX',
        priority: 50
      }
    ],
    
    calculation: {
      productPrice: 549.99,
      quantity: 1,
      
      exciseTaxes: [
        {
          ruleId: 'excise_federal_firearms',
          ruleName: 'Federal Firearms Excise Tax',
          calculation: '$549.99 × 10% = $55.00',
          amount: 55.00
        }
      ],
      
      salesTaxes: [
        {
          ruleId: 'sales_tx_general',
          ruleName: 'Texas General Sales Tax',
          calculation: '($549.99 + $55.00) × 6.25% = $37.81',
          taxableBase: 604.99,
          amount: 37.81
        }
      ],
      
      totalExciseTax: 55.00,
      totalSalesTax: 37.81,
      totalTax: 92.81,
      finalTotal: 642.80
    },
    
    status: 'completed',
    hasErrors: false,
    hasWarnings: false,
    flags: ['high_regulation'],
    
    processingTime: '52ms',
    apiVersion: '2.1.0',
    requestId: 'req_20241223_15452_abc'
  },
  
  {
    id: 'txn_20241223_003',
    timestamp: '2024-12-23T16:12:08Z',
    merchantId: 'merchant_tobacco_ca',
    merchantName: 'Golden State Tobacco',
    
    product: {
      id: 'prod_cigarette_marlboro',
      name: 'Marlboro Red Cigarettes',
      sku: 'MAR-RED-PACK',
      description: 'Pack of 20 cigarettes, premium tobacco'
    },
    
    aiCategorization: {
      confidence: 0.72,
      suggestedCategoryId: 'TOBACCO_CIGARETTES',
      suggestedCategoryPath: 'Tobacco Products > Cigarettes',
      flags: ['low_confidence'],
      processingTime: '445ms'
    },
    
    selectedCategory: {
      id: 'TOBACCO_CIGARETTES',
      name: 'Cigarettes',
      path: 'Tobacco Products > Cigarettes',
      source: 'admin_review' // This was flagged and reviewed by admin
    },
    
    applicableRules: [
      {
        id: 'excise_federal_cigarettes',
        name: 'Federal Cigarette Excise Tax',
        type: 'excise',
        jurisdiction: 'Federal',
        rate: 1.01,
        unit: '1pack',
        calculationMethod: 'specific',
        matchReason: 'Category match: TOBACCO_CIGARETTES',
        priority: 100
      },
      {
        id: 'excise_ca_cigarettes',
        name: 'California Cigarette Excise Tax',
        type: 'excise',
        jurisdiction: 'CA',
        rate: 0.87,
        unit: '1pack',
        calculationMethod: 'specific',
        matchReason: 'Geographic + Category match',
        priority: 85
      },
      {
        id: 'sales_ca_general',
        name: 'California General Sales Tax',
        type: 'sales',
        jurisdiction: 'CA',
        rate: 7.25,
        unit: '%',
        calculationMethod: 'ad_valorem',
        matchReason: 'Default sales tax for CA',
        priority: 50
      }
    ],
    
    calculation: {
      productPrice: 12.99,
      quantity: 1,
      
      exciseTaxes: [
        {
          ruleId: 'excise_federal_cigarettes',
          ruleName: 'Federal Cigarette Excise Tax',
          calculation: '1 pack × $1.01/pack = $1.01',
          amount: 1.01
        },
        {
          ruleId: 'excise_ca_cigarettes',
          ruleName: 'California Cigarette Excise Tax',
          calculation: '1 pack × $0.87/pack = $0.87',
          amount: 0.87
        }
      ],
      
      salesTaxes: [
        {
          ruleId: 'sales_ca_general',
          ruleName: 'California General Sales Tax',
          calculation: '($12.99 + $1.01 + $0.87) × 7.25% = $1.08',
          taxableBase: 14.87,
          amount: 1.08
        }
      ],
      
      totalExciseTax: 1.88,
      totalSalesTax: 1.08,
      totalTax: 2.96,
      finalTotal: 15.95
    },
    
    status: 'completed',
    hasErrors: false,
    hasWarnings: true,
    flags: ['low_confidence', 'admin_reviewed'],
    
    processingTime: '234ms',
    apiVersion: '2.1.0',
    requestId: 'req_20241223_16120_def'
  },
  
  {
    id: 'txn_20241223_004',
    timestamp: '2024-12-23T17:22:33Z',
    merchantId: 'merchant_liquor_il',
    merchantName: 'Chicago Premium Spirits',
    
    product: {
      id: 'prod_whiskey_bourbon',
      name: 'Buffalo Trace Bourbon Whiskey',
      sku: 'BT-BOURBON-750',
      description: '750ml bottle, 45% ABV Kentucky straight bourbon'
    },
    
    aiCategorization: {
      confidence: 0.88,
      suggestedCategoryId: 'ALCOHOL_SPIRITS',
      suggestedCategoryPath: 'Alcoholic Beverages > Spirits',
      flags: [],
      processingTime: '198ms'
    },
    
    selectedCategory: {
      id: 'ALCOHOL_SPIRITS',
      name: 'Spirits',
      path: 'Alcoholic Beverages > Spirits',
      source: 'ai_suggestion'
    },
    
    applicableRules: [
      {
        id: 'excise_federal_spirits',
        name: 'Federal Distilled Spirits Excise Tax',
        type: 'excise',
        jurisdiction: 'Federal',
        rate: 13.50,
        unit: '1proof_gallon',
        calculationMethod: 'specific',
        matchReason: 'Category match: ALCOHOL_SPIRITS',
        priority: 100
      },
      {
        id: 'excise_il_spirits',
        name: 'Illinois Spirits Excise Tax',
        type: 'excise',
        jurisdiction: 'IL',
        rate: 8.55,
        unit: '1gallon',
        calculationMethod: 'specific',
        matchReason: 'Geographic + Category match',
        priority: 80
      },
      {
        id: 'sales_cook_county',
        name: 'Cook County Sales Tax',
        type: 'sales',
        jurisdiction: 'Cook County, IL',
        rate: 10.75,
        unit: '%',
        calculationMethod: 'ad_valorem',
        matchReason: 'Geographic match: Cook County',
        priority: 60
      }
    ],
    
    calculation: {
      productPrice: 45.99,
      quantity: 1,
      
      exciseTaxes: [
        {
          ruleId: 'excise_federal_spirits',
          ruleName: 'Federal Distilled Spirits Excise Tax',
          calculation: '0.75L × 45% ABV = 0.3375 proof gallons × $13.50 = $4.56',
          amount: 4.56
        },
        {
          ruleId: 'excise_il_spirits',
          ruleName: 'Illinois Spirits Excise Tax',
          calculation: '0.75L × $8.55/gallon = $1.70',
          amount: 1.70
        }
      ],
      
      salesTaxes: [
        {
          ruleId: 'sales_cook_county',
          ruleName: 'Cook County Sales Tax',
          calculation: '($45.99 + $4.56 + $1.70) × 10.75% = $5.59',
          taxableBase: 52.25,
          amount: 5.59
        }
      ],
      
      totalExciseTax: 6.26,
      totalSalesTax: 5.59,
      totalTax: 11.85,
      finalTotal: 57.84
    },
    
    status: 'completed',
    hasErrors: false,
    hasWarnings: false,
    flags: [],
    
    processingTime: '89ms',
    apiVersion: '2.1.0',
    requestId: 'req_20241223_17223_ghi'
  },
  
  {
    id: 'txn_20241223_005',
    timestamp: '2024-12-23T18:15:47Z',
    merchantId: 'merchant_general_ny',
    merchantName: 'Brooklyn Corner Store',
    
    product: {
      id: 'prod_error_unknown',
      name: 'Unknown Product Item #XYZ123',
      sku: 'UNK-XYZ-123',
      description: 'Product data incomplete or corrupted'
    },
    
    aiCategorization: {
      confidence: 0.23,
      suggestedCategoryId: null,
      suggestedCategoryPath: null,
      flags: ['categorization_failed', 'insufficient_data'],
      processingTime: '1234ms'
    },
    
    selectedCategory: {
      id: 'GENERAL_MERCHANDISE',
      name: 'General Merchandise',
      path: 'General > Merchandise',
      source: 'fallback_default'
    },
    
    applicableRules: [
      {
        id: 'sales_ny_general',
        name: 'New York General Sales Tax',
        type: 'sales',
        jurisdiction: 'NY',
        rate: 8.25,
        unit: '%',
        calculationMethod: 'ad_valorem',
        matchReason: 'Default fallback rule',
        priority: 50
      }
    ],
    
    calculation: {
      productPrice: 19.99,
      quantity: 1,
      
      exciseTaxes: [],
      
      salesTaxes: [
        {
          ruleId: 'sales_ny_general',
          ruleName: 'New York General Sales Tax',
          calculation: '$19.99 × 8.25% = $1.65',
          taxableBase: 19.99,
          amount: 1.65
        }
      ],
      
      totalExciseTax: 0.00,
      totalSalesTax: 1.65,
      totalTax: 1.65,
      finalTotal: 21.64
    },
    
    status: 'completed_with_errors',
    hasErrors: true,
    hasWarnings: true,
    flags: ['categorization_failed', 'insufficient_data', 'fallback_used'],
    
    processingTime: '1456ms',
    apiVersion: '2.1.0',
    requestId: 'req_20241223_18154_jkl'
  }
];

function TaxAudit() {
  const [transactions, setTransactions] = useState(mockTaxCalculationTransactions);
  const [filteredTransactions, setFilteredTransactions] = useState(mockTaxCalculationTransactions);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [merchantFilter, setMerchantFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [flagFilter, setFlagFilter] = useState('all');

  useEffect(() => {
    applyFilters();
  }, [searchTerm, statusFilter, merchantFilter, categoryFilter, dateRange, flagFilter, transactions]);

  const applyFilters = () => {
    let filtered = [...transactions];

    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(txn => 
        txn.product.name.toLowerCase().includes(term) ||
        txn.product.sku.toLowerCase().includes(term) ||
        txn.merchantName.toLowerCase().includes(term) ||
        txn.requestId.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(txn => txn.status === statusFilter);
    }

    // Merchant filter
    if (merchantFilter !== 'all') {
      filtered = filtered.filter(txn => txn.merchantId === merchantFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(txn => txn.selectedCategory.id === categoryFilter);
    }

    // Flag filter
    if (flagFilter !== 'all') {
      if (flagFilter === 'errors') {
        filtered = filtered.filter(txn => txn.hasErrors);
      } else if (flagFilter === 'warnings') {
        filtered = filtered.filter(txn => txn.hasWarnings);
      } else if (flagFilter === 'flagged') {
        filtered = filtered.filter(txn => txn.flags.length > 0);
      }
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.timestamp);
        return txnDate >= start && txnDate <= end;
      });
    }

    setFilteredTransactions(filtered);
  };

  const getStatusTag = (status, hasErrors, hasWarnings) => {
    if (hasErrors) {
      return <Tag color="red" icon={<CloseCircleOutlined />}>Error</Tag>;
    }
    if (hasWarnings) {
      return <Tag color="orange" icon={<WarningOutlined />}>Warning</Tag>;
    }
    if (status === 'completed') {
      return <Tag color="green" icon={<CheckCircleOutlined />}>Completed</Tag>;
    }
    return <Tag color="blue" icon={<InfoCircleOutlined />}>{status}</Tag>;
  };

  const getFlagTags = (flags) => {
    const flagColors = {
      'low_confidence': 'orange',
      'high_regulation': 'purple',
      'admin_reviewed': 'blue',
      'categorization_failed': 'red',
      'insufficient_data': 'red',
      'fallback_used': 'orange'
    };

    return flags.map(flag => (
      <Tag key={flag} color={flagColors[flag] || 'default'} size="small">
        {flag.replace('_', ' ')}
      </Tag>
    ));
  };

  const renderDecisionTree = (transaction) => {
    const treeData = [
      {
        title: (
          <div className="decision-node">
            <strong>Product Input</strong>
            <div className="node-details">
              <Text type="secondary">{transaction.product.name}</Text>
              <br />
              <Text code>{transaction.product.sku}</Text>
            </div>
          </div>
        ),
        key: 'product',
        children: [
          {
            title: (
              <div className="decision-node">
                <strong>AI Categorization</strong>
                <div className="node-details">
                  <Text>Confidence: <strong>{(transaction.aiCategorization.confidence * 100).toFixed(0)}%</strong></Text>
                  <br />
                  <Text>Suggested: {transaction.aiCategorization.suggestedCategoryPath || 'None'}</Text>
                  <br />
                  <Text type="secondary">Processing: {transaction.aiCategorization.processingTime}</Text>
                  {transaction.aiCategorization.flags.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      {getFlagTags(transaction.aiCategorization.flags)}
                    </div>
                  )}
                </div>
              </div>
            ),
            key: 'ai-categorization',
            children: [
              {
                title: (
                  <div className="decision-node">
                    <strong>Selected Category</strong>
                    <div className="node-details">
                      <Text>{transaction.selectedCategory.path}</Text>
                      <br />
                      <Tag color={transaction.selectedCategory.source === 'ai_suggestion' ? 'green' : 
                                 transaction.selectedCategory.source === 'admin_review' ? 'blue' : 'orange'}>
                        {transaction.selectedCategory.source.replace('_', ' ')}
                      </Tag>
                    </div>
                  </div>
                ),
                key: 'selected-category',
                children: [
                  {
                    title: (
                      <div className="decision-node">
                        <strong>Tax Rules Applied</strong>
                        <div className="node-details">
                          <Text>{transaction.applicableRules.length} rules matched</Text>
                        </div>
                      </div>
                    ),
                    key: 'tax-rules',
                    children: transaction.applicableRules.map((rule, index) => ({
                      title: (
                        <div className="decision-node">
                          <strong>{rule.name}</strong>
                          <div className="node-details">
                            <Text type="secondary">{rule.jurisdiction} • Priority: {rule.priority}</Text>
                            <br />
                            <Text>Rate: {rule.rate}{rule.unit}</Text>
                            <br />
                            <Text type="secondary">{rule.matchReason}</Text>
                          </div>
                        </div>
                      ),
                      key: `rule-${index}`
                    })),
                  }
                ]
              }
            ]
          }
        ]
      }
    ];

    return (
      <Card title="Decision Tree Visualization" className="decision-tree-card">
        <Tree
          treeData={treeData}
          defaultExpandAll
          showLine={{ showLeafIcon: false }}
          showIcon={false}
        />
      </Card>
    );
  };

  const renderCalculationBreakdown = (transaction) => {
    return (
      <Card title="Tax Calculation Breakdown" className="calculation-breakdown">
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Statistic
              title="Product Price"
              value={transaction.calculation.productPrice}
              prefix="$"
              precision={2}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Excise Tax"
              value={transaction.calculation.totalExciseTax}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Sales Tax"
              value={transaction.calculation.totalSalesTax}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={12}>
            <Card size="small" title="Excise Tax Details">
              {transaction.calculation.exciseTaxes.map((tax, index) => (
                <div key={index} style={{ marginBottom: 12 }}>
                  <Text strong>{tax.ruleName}</Text>
                  <br />
                  <Text type="secondary">{tax.calculation}</Text>
                  <br />
                  <Text>Amount: <strong>${tax.amount.toFixed(2)}</strong></Text>
                </div>
              ))}
              {transaction.calculation.exciseTaxes.length === 0 && (
                <Text type="secondary">No excise taxes applied</Text>
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" title="Sales Tax Details">
              {transaction.calculation.salesTaxes.map((tax, index) => (
                <div key={index} style={{ marginBottom: 12 }}>
                  <Text strong>{tax.ruleName}</Text>
                  <br />
                  <Text type="secondary">{tax.calculation}</Text>
                  <br />
                  <Text>Taxable Base: <strong>${tax.taxableBase.toFixed(2)}</strong></Text>
                  <br />
                  <Text>Amount: <strong>${tax.amount.toFixed(2)}</strong></Text>
                </div>
              ))}
            </Card>
          </Col>
        </Row>

        <div style={{ marginTop: 24, padding: 16, backgroundColor: '#f0f2f5', borderRadius: 6 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Text type="secondary">Product Price:</Text>
              <br />
              <Text strong>${transaction.calculation.productPrice.toFixed(2)}</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Excise Tax:</Text>
              <br />
              <Text strong>+${transaction.calculation.totalExciseTax.toFixed(2)}</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Sales Tax:</Text>
              <br />
              <Text strong>+${transaction.calculation.totalSalesTax.toFixed(2)}</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Final Total:</Text>
              <br />
              <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                ${transaction.calculation.finalTotal.toFixed(2)}
              </Text>
            </Col>
          </Row>
        </div>
      </Card>
    );
  };

  const exportData = () => {
    const dataToExport = filteredTransactions.map(txn => ({
      'Transaction ID': txn.id,
      'Timestamp': txn.timestamp,
      'Merchant': txn.merchantName,
      'Product': txn.product.name,
      'SKU': txn.product.sku,
      'Category': txn.selectedCategory.path,
      'Product Price': txn.calculation.productPrice,
      'Excise Tax': txn.calculation.totalExciseTax,
      'Sales Tax': txn.calculation.totalSalesTax,
      'Total Tax': txn.calculation.totalTax,
      'Final Total': txn.calculation.finalTotal,
      'Status': txn.status,
      'Flags': txn.flags.join(', '),
      'Processing Time': txn.processingTime,
      'Request ID': txn.requestId
    }));

    const csv = [
      Object.keys(dataToExport[0]).join(','),
      ...dataToExport.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax-audit-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id) => <Text code>{id}</Text>
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp) => new Date(timestamp).toLocaleString()
    },
    {
      title: 'Merchant',
      dataIndex: 'merchantName',
      key: 'merchantName',
      width: 180
    },
    {
      title: 'Product',
      key: 'product',
      width: 200,
      render: (_, record) => (
        <div>
          <Text strong>{record.product.name}</Text>
          <br />
          <Text type="secondary" code>{record.product.sku}</Text>
        </div>
      )
    },
    {
      title: 'Category',
      key: 'category',
      width: 160,
      render: (_, record) => (
        <div>
          <Text>{record.selectedCategory.name}</Text>
          <br />
          <Tag color={record.selectedCategory.source === 'ai_suggestion' ? 'green' : 
                     record.selectedCategory.source === 'admin_review' ? 'blue' : 'orange'} size="small">
            {record.selectedCategory.source.replace('_', ' ')}
          </Tag>
        </div>
      )
    },
    {
      title: 'Total Tax',
      key: 'totalTax',
      width: 120,
      render: (_, record) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${record.calculation.totalTax.toFixed(2)}
        </Text>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => getStatusTag(record.status, record.hasErrors, record.hasWarnings)
    },
    {
      title: 'Flags',
      key: 'flags',
      width: 180,
      render: (_, record) => (
        <div>
          {getFlagTags(record.flags)}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedTransaction(record);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const getUniqueValues = (field) => {
    return [...new Set(transactions.map(txn => {
      if (field === 'merchant') return txn.merchantId;
      if (field === 'category') return txn.selectedCategory.id;
      return txn[field];
    }))];
  };

  return (
    <div className="tax-audit-container">
      <div className="audit-header">
        <Title level={2}>Tax Audit Log Viewer</Title>
        <Text type="secondary">
          Tax calculation transaction records with complete decision tree traceability
        </Text>
      </div>

      {/* Statistics Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Transactions"
              value={filteredTransactions.length}
              prefix={<BranchesOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Transactions with Errors"
              value={filteredTransactions.filter(t => t.hasErrors).length}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Transactions with Warnings"
              value={filteredTransactions.filter(t => t.hasWarnings).length}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Average Processing Time"
              value={Math.round(transactions.reduce((sum, t) => sum + parseInt(t.processingTime), 0) / transactions.length)}
              suffix="ms"
              prefix={<InfoCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Advanced Filter Panel */}
      <Card title="Advanced Filters" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Input
              placeholder="Search transactions..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Status</Option>
              <Option value="completed">Completed</Option>
              <Option value="completed_with_errors">With Errors</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Merchant"
              value={merchantFilter}
              onChange={setMerchantFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Merchants</Option>
              {getUniqueValues('merchant').map(id => {
                const merchant = transactions.find(t => t.merchantId === id);
                return (
                  <Option key={id} value={id}>
                    {merchant?.merchantName}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Categories</Option>
              {getUniqueValues('category').map(id => {
                const transaction = transactions.find(t => t.selectedCategory.id === id);
                return (
                  <Option key={id} value={id}>
                    {transaction?.selectedCategory.name}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Flags"
              value={flagFilter}
              onChange={setFlagFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Flags</Option>
              <Option value="errors">Errors Only</Option>
              <Option value="warnings">Warnings Only</Option>
              <Option value="flagged">Flagged Only</Option>
            </Select>
          </Col>
          <Col span={2}>
            <Button
              type="primary"
              icon={<ExportOutlined />}
              onClick={exportData}
              style={{ width: '100%' }}
            >
              Export
            </Button>
          </Col>
        </Row>
        <Row style={{ marginTop: 16 }}>
          <Col span={8}>
            <RangePicker
              placeholder={['Start Date', 'End Date']}
              value={dateRange}
              onChange={setDateRange}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Transaction List */}
      <Card title="Tax Calculation Transactions">
        <Table
          columns={columns}
          dataSource={filteredTransactions}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Transaction Detail Drawer */}
      <Drawer
        title={selectedTransaction ? `Transaction Details: ${selectedTransaction.id}` : ''}
        width={1000}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedTransaction && (
          <div>
            {/* Transaction Overview */}
            <Card title="Transaction Overview" style={{ marginBottom: 16 }}>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Transaction ID">{selectedTransaction.id}</Descriptions.Item>
                <Descriptions.Item label="Request ID">{selectedTransaction.requestId}</Descriptions.Item>
                <Descriptions.Item label="Timestamp">{new Date(selectedTransaction.timestamp).toLocaleString()}</Descriptions.Item>
                <Descriptions.Item label="Processing Time">{selectedTransaction.processingTime}</Descriptions.Item>
                <Descriptions.Item label="Merchant">{selectedTransaction.merchantName}</Descriptions.Item>
                <Descriptions.Item label="Merchant ID">{selectedTransaction.merchantId}</Descriptions.Item>
                <Descriptions.Item label="API Version">{selectedTransaction.apiVersion}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  {getStatusTag(selectedTransaction.status, selectedTransaction.hasErrors, selectedTransaction.hasWarnings)}
                </Descriptions.Item>
              </Descriptions>
              
              {selectedTransaction.flags.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Flags: </Text>
                  {getFlagTags(selectedTransaction.flags)}
                </div>
              )}
            </Card>

            {/* Product Information */}
            <Card title="Product Information" style={{ marginBottom: 16 }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Product Name">{selectedTransaction.product.name}</Descriptions.Item>
                <Descriptions.Item label="SKU">{selectedTransaction.product.sku}</Descriptions.Item>
                <Descriptions.Item label="Description">{selectedTransaction.product.description}</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Decision Tree */}
            {renderDecisionTree(selectedTransaction)}

            {/* Calculation Breakdown */}
            <div style={{ marginTop: 16 }}>
              {renderCalculationBreakdown(selectedTransaction)}
            </div>

            {/* Processing Timeline */}
            <Card title="Processing Timeline" style={{ marginTop: 16 }}>
              <Timeline>
                <Timeline.Item color="blue">
                  <Text strong>Product Input Received</Text>
                  <br />
                  <Text type="secondary">Product data validated and parsed</Text>
                </Timeline.Item>
                <Timeline.Item color={selectedTransaction.aiCategorization.confidence > 0.8 ? "green" : "orange"}>
                  <Text strong>AI Categorization</Text>
                  <br />
                  <Text type="secondary">
                    Confidence: {(selectedTransaction.aiCategorization.confidence * 100).toFixed(0)}% • 
                    Time: {selectedTransaction.aiCategorization.processingTime}
                  </Text>
                </Timeline.Item>
                <Timeline.Item color="green">
                  <Text strong>Category Selected</Text>
                  <br />
                  <Text type="secondary">
                    {selectedTransaction.selectedCategory.path} • 
                    Source: {selectedTransaction.selectedCategory.source}
                  </Text>
                </Timeline.Item>
                <Timeline.Item color="purple">
                  <Text strong>Tax Rules Applied</Text>
                  <br />
                  <Text type="secondary">{selectedTransaction.applicableRules.length} rules matched and processed</Text>
                </Timeline.Item>
                <Timeline.Item color="green">
                  <Text strong>Calculation Complete</Text>
                  <br />
                  <Text type="secondary">
                    Total processing time: {selectedTransaction.processingTime}
                  </Text>
                </Timeline.Item>
              </Timeline>
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default TaxAudit;